import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, CheckCircle, WarningCircle } from '@phosphor-icons/react';

const FORMSPREE_URL = 'https://formspree.io/f/maqreogr';

export default function ContactForm({ code, tripName, prefillMessage, onClose }) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState(prefillMessage || '');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) { setError('All fields are required'); return; }
    if (!isValidEmail(email)) { setError('Please enter a valid email'); return; }
    setSubmitting(true);
    setError('');
    try {
      const resp = await fetch(FORMSPREE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          message: message.trim(),
          _subject: `Fore Tour — Trip ${code} (${tripName})`,
        }),
      });
      if (resp.ok) {
        setSubmitted(true);
      } else {
        setError('Failed to send. Please try again or use the email link below.');
      }
    } catch {
      setError('Network error. Please try the email link below.');
    } finally { setSubmitting(false); }
  };

  const mailtoUrl = `mailto:acottrill@foretour.co.uk?subject=${encodeURIComponent(`Fore Tour — Trip ${code}`)}&body=${encodeURIComponent(message)}`;

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F4F6F5] flex items-center justify-center px-4" data-testid="contact-success">
        <div className="w-full max-w-sm bg-white rounded-2xl border border-[#E1E6E4] p-6 text-center space-y-4">
          <CheckCircle className="w-12 h-12 text-[#1B4332] mx-auto" weight="fill" />
          <h2 className="text-lg font-bold text-[#1D242B]">Message Sent</h2>
          <p className="text-sm text-[#455561]">We'll be in touch shortly.</p>
          <Button onClick={onClose || (() => navigate(`/trip/${code}`))}
            className="w-full h-12 rounded-xl bg-[#1B4332] hover:bg-[#163B2B] text-white">
            Back to Trip
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F6F5]" data-testid="contact-form">
      <div className="p-4 flex items-center gap-3 max-w-lg mx-auto">
        <button onClick={onClose || (() => navigate(`/trip/${code}`))} className="w-10 h-10 rounded-full bg-white border border-[#E1E6E4] flex items-center justify-center">
          <ArrowLeft className="w-5 h-5 text-[#1D242B]" weight="bold" />
        </button>
        <h1 className="text-xl font-bold text-[#1D242B]">Contact Fore Tour</h1>
      </div>
      <form onSubmit={handleSubmit} className="px-4 max-w-lg mx-auto space-y-4 pb-8">
        <div>
          <label className="text-sm font-semibold text-[#455561] mb-1.5 block">Name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
            className="h-12 rounded-xl text-base" data-testid="contact-name" />
        </div>
        <div>
          <label className="text-sm font-semibold text-[#455561] mb-1.5 block">Email</label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
            className="h-12 rounded-xl text-base" data-testid="contact-email" />
        </div>
        <div>
          <label className="text-sm font-semibold text-[#455561] mb-1.5 block">Message</label>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={6}
            className="w-full rounded-xl border border-[#E1E6E4] p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#1B4332]"
            data-testid="contact-message" />
        </div>
        {error && (
          <div className="flex items-start gap-2 text-sm text-[#D32F2F]" data-testid="contact-error">
            <WarningCircle className="w-4 h-4 mt-0.5 flex-shrink-0" weight="bold" />
            <div>
              <p>{error}</p>
              <a href={mailtoUrl} className="underline text-[#1B4332] text-xs mt-1 block">Email us directly</a>
            </div>
          </div>
        )}
        <Button type="submit" disabled={submitting}
          className="w-full h-14 bg-[#1B4332] hover:bg-[#163B2B] text-white rounded-xl text-base font-bold" data-testid="contact-submit">
          {submitting ? 'Sending...' : 'Send Message'}
        </Button>
      </form>
    </div>
  );
}
